import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Users, Activity, ClipboardCheck, Bell, Shield, AlertTriangle, Bug } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SentryTestButton } from "@/components/SentryTestButton";

const AdminDashboard = () => {
  // Fetch counts for dashboard
  const { data: userCount } = useQuery({
    queryKey: ['admin-user-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('app_users')
        .select('*', { count: 'exact', head: true });
      return count || 0;
    }
  });

  const { data: activityCount } = useQuery({
    queryKey: ['admin-activity-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('activity_logs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      return count || 0;
    }
  });

  const { data: notificationCount } = useQuery({
    queryKey: ['admin-notification-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('notification_event_types')
        .select('*', { count: 'exact', head: true });
      return count || 0;
    }
  });

  const adminModules = [
    {
      title: "User Management",
      description: "Manage user accounts, roles, and permissions",
      icon: Users,
      path: "/admin/users",
      stat: userCount,
      statLabel: "Total Users"
    },
    {
      title: "Activity Logs",
      description: "View system activity and audit trails",
      icon: Activity,
      path: "/admin/activity-logs",
      stat: activityCount,
      statLabel: "Last 24h"
    },
    {
      title: "Data Quality",
      description: "Manage duplicates for candidates, companies, and skills",
      icon: ClipboardCheck,
      path: "/admin/data-quality",
      stat: null,
      statLabel: null
    },
    {
      title: "Notification System",
      description: "Configure event types, templates, and delivery settings",
      icon: Bell,
      path: "/admin/notifications",
      stat: notificationCount,
      statLabel: "Event Types"
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-destructive/10 rounded-lg">
          <Shield className="h-8 w-8 text-destructive" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-muted-foreground">System administration and configuration</p>
        </div>
      </div>

      {/* Warning Banner */}
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="flex items-center gap-4 py-4">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">
            You are in the Admin Panel. Changes made here affect the entire system.
          </p>
        </CardContent>
      </Card>

      {/* Sentry Test Section */}
      <Card className="border-orange-500/50 bg-orange-500/5">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-lg">Sentry Error Tracking Test</CardTitle>
          </div>
          <CardDescription>
            Click the button below to trigger a test error and verify Sentry is capturing errors correctly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SentryTestButton />
        </CardContent>
      </Card>

      {/* Admin Modules Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {adminModules.map((module) => {
          const Icon = module.icon;
          return (
            <Card key={module.path} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  {module.stat !== null && (
                    <div className="text-right">
                      <p className="text-2xl font-bold">{module.stat}</p>
                      <p className="text-xs text-muted-foreground">{module.statLabel}</p>
                    </div>
                  )}
                </div>
                <CardTitle className="mt-4">{module.title}</CardTitle>
                <CardDescription>{module.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to={module.path}>
                  <Button className="w-full">
                    Open
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDashboard;
